import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Space, Typography } from 'antd';
import { useHistory } from 'react-router-dom';

import { DndContext, DragOverlay, useDndMonitor } from '@dnd-kit/core';
import Draggable from '../Components/Draggable';
import Droppable from '../Components/Droppable';
import "../styles/drag-drop.scss";

import AddVideoToVideoBank from '../Components/AddVideoToVideoBank';
import VideoRow from '../Components/VideoRow';
import ModuleConfigForm from '../Components/ModuleConfigForm';
import {
    PlaySquareOutlined
} from "@ant-design/icons";
import { connect } from 'react-redux';
import { API_URL } from '../const';
const { Text, Title } = Typography;

const DragMonitor = ({ setDragMeta }) => {
    const last = React.useRef({ activeId: null, overId: null });

    const updateMeta = ({ active, over }) => {
        const activeData = active?.data?.current || null;
        const overData = over?.data?.current || null;

        const activeId = active?.id ?? null;
        const overId = over?.id ?? null;

        if (
            last.current.activeId === activeId &&
            last.current.overId === overId
        ) {
            return;
        }

        last.current = { activeId, overId };

        setDragMeta({
            active: activeData,
            over: overData,
        });
    };

    useDndMonitor({
        onDragStart: updateMeta,
        onDragMove: updateMeta,
        onDragOver: updateMeta,
        onDragEnd: () => {
            setDragMeta({ active: null, over: null });
            last.current = { activeId: null, overId: null };
        },
        onDragCancel: () => {
            setDragMeta({ active: null, over: null });
            last.current = { activeId: null, overId: null };
        },
    });
    return null;
};

const ModuleDetailsStep = ({ allVideos, authorsDict, collectionCategories, currentUser, goToNextStep, goToPreviousStep, module, policyModel, updateState, videoBank, index = null, mode = "new" }) => {

    const history = useHistory();
    const [tempModuleState, updateTempModuleState] = useState(module);
    const [tempVideoBankState, updateVideoBankState] = useState(videoBank);
    const [tempAllVideosState, updateTempAllVideosState] = useState(allVideos);
    const [dragMeta, setDragMeta] = useState({ active: null, over: null });

    useEffect(() => {
        updateVideoBankState(videoBank);
    }, [videoBank])

    const handleOnEditModuleFormFinish = (updatedModule) => {
        if (mode === "edit") {
            return;
        }
        updateState("module", updatedModule);
        updateState("videoBank", tempVideoBankState);
        goToNextStep();
    };

    const handleOnDragEnd = ({ active, over }) => {
        if (!over) return;

        const activeData = active?.data?.current || {};
        const overData = over?.data?.current || {};

        const sourceContainer = activeData.container;
        const destinationContainer = overData.container;
        const sourceIndex = activeData.index;
        const destinationIndex = overData.index;

        if (sourceContainer == null || destinationContainer == null) return;
        if (destinationIndex == null || sourceIndex == null) return;

        if (sourceContainer === destinationContainer) {
            if (sourceContainer === "videos") {
                const updatedVideos = Array.from(tempModuleState.videos);
                const [movedVideo] = updatedVideos.splice(sourceIndex, 1);
                updatedVideos.splice(destinationIndex, 0, movedVideo);
                updateTempModuleState({
                    ...tempModuleState,
                    videos: updatedVideos,
                });
            } else if (sourceContainer === "videoBank") {
                const updatedVideoBank = Array.from(tempVideoBankState);
                const [movedVideo] = updatedVideoBank.splice(sourceIndex, 1);
                updatedVideoBank.splice(destinationIndex, 0, movedVideo);
                updateVideoBankState(updatedVideoBank);
            }
            return;
        }

        if (sourceContainer === "videos" && destinationContainer === "videoBank") {
            const updatedVideos = Array.from(tempModuleState.videos);
            const [movedVideo] = updatedVideos.splice(sourceIndex, 1);
            const updatedVideoBank = Array.from(tempVideoBankState);
            updatedVideoBank.splice(destinationIndex, 0, movedVideo);
            updateTempModuleState({
                ...tempModuleState,
                videos: updatedVideos,
            });
            updateVideoBankState(updatedVideoBank);
        } else if (sourceContainer === "videoBank" && destinationContainer === "videos") {
            const updatedVideoBank = Array.from(tempVideoBankState);
            const [movedVideo] = updatedVideoBank.splice(sourceIndex, 1);
            const updatedVideos = Array.from(tempModuleState.videos);
            updatedVideos.splice(destinationIndex, 0, movedVideo);
            updateTempModuleState({
                ...tempModuleState,
                videos: updatedVideos,
            });
            updateVideoBankState(updatedVideoBank);
        }
    };

    const addVideosToVideoBank = (videos) => {
        let newVideos = []
        videos.forEach(video => {
            let found = tempAllVideosState.findIndex(existingVideo => existingVideo.embedId === video.embedId);
            if (found === -1) newVideos.push(video);
        })

        updateVideoBankState([
            ...tempVideoBankState,
            ...newVideos
        ])
        updateTempAllVideosState([
            ...tempAllVideosState,
            ...newVideos
        ])
    }

    const fetchCreatorList = async (username) => {
        return fetch(API_URL + `/api/v1/creators/${username ? "?search=" + username : ""}`, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + localStorage.getItem('access'),
                'Content-Type': 'application/json'
            }
        })
            .then((response) => response.json())
            .then((body) => {

                let options = []
                let authors = {}
                body.forEach((user) => {

                    authors[user.username] = user;

                    options.push({
                        label: `${user.firstName} ${user.lastName}`,
                        value: user.username
                    })
                })

                updateState("authorsDict", {
                    ...authorsDict,
                    ...authors
                })
                return options

            });
    }

    const getPlaceholderIndex = useCallback((containerKey, length) => {
        const over = dragMeta.over;
        if (!over || over.container !== containerKey) return null;
        const overIndex = over.index ?? length;
        return Math.max(0, Math.min(length, overIndex));
    }, [dragMeta.over]);

    const renderPlaceholder = (key) => (
        <div key={key} className="droppable-placeholder-manual drag-placeholder" />
    );

    const getActiveItem = useCallback(() => {
        const active = dragMeta.active;
        if (!active || active.index == null || !active.container) return null;
        if (active.container === "videoBank") return tempVideoBankState[active.index] || null;
        if (active.container === "videos") return tempModuleState.videos[active.index] || null;
        return null;
    }, [dragMeta.active, tempModuleState.videos, tempVideoBankState]);

    const isActiveItem = useCallback(
        (container, index) =>
            dragMeta.active?.container === container && dragMeta.active?.index === index,
        [dragMeta.active]
    );

    const videoBankPlaceholderIndex = useMemo(
        () => getPlaceholderIndex("videoBank", tempVideoBankState.length),
        [getPlaceholderIndex, tempVideoBankState.length]
    );

    const videosPlaceholderIndex = useMemo(
        () => getPlaceholderIndex("videos", tempModuleState.videos.length),
        [getPlaceholderIndex, tempModuleState.videos.length]
    );

    // Update video title in video bank
    const updateVideoTitleInVideoBank = (newTitle, video, videoIndex) => {
        // Update video bank entry (by embedId fallback to provided index)
        const newVideoBankState = Array.from(tempVideoBankState);
        const bankIndex =
            newVideoBankState.findIndex((v) => v.embedId === video.embedId) ??
            videoIndex;
        if (bankIndex > -1) {
            newVideoBankState[bankIndex] = {
                ...newVideoBankState[bankIndex],
                title: newTitle,
            };
            updateVideoBankState(newVideoBankState);
        }

        // Update module videos entry if present
        const newModuleVideos = Array.from(tempModuleState.videos || []);
        const moduleIndex = newModuleVideos.findIndex((v) => v.embedId === video.embedId);
        if (moduleIndex > -1) {
            newModuleVideos[moduleIndex] = {
                ...newModuleVideos[moduleIndex],
                title: newTitle,
            };
            updateTempModuleState({
                ...tempModuleState,
                videos: newModuleVideos,
            });
        }

    };

    return <div style={{ padding: "10px 0px" }}>
        <DndContext onDragEnd={handleOnDragEnd}>
            <DragMonitor setDragMeta={setDragMeta} />
            <div className="new-module-container">

                <div className="video-bank-container">
                    <div className="video-bank-header">
                        <Space direction='vertical'>
                            <div className="video-bank-header">
                                <Title level={4} style={{ margin: 0 }}>
                                    Video Bank
                                    <Text type="secondary" className="ml-5" italic style={{ fontSize: "12px" }}>
                                        {tempVideoBankState.length > 0 && (
                                            <>
                                                ({tempVideoBankState.length} video{tempVideoBankState.length > 1 ? 's' : ''})
                                            </>
                                        )}
                                    </Text>
                                </Title>
                            </div>
                        </Space>
                        <div className="mt-10">
                            <AddVideoToVideoBank
                                addVideosToVideoBank={addVideosToVideoBank}
                            />
                        </div>
                    </div>



                    <div className="video-bank">
                        {tempVideoBankState.length === 0 ? (
                            <div className="no-videos-in-bank">
                                <PlaySquareOutlined />
                                <Text type="secondary">
                                    Your video bank is empty. <br />
                                    Add videos from YouTube or Vimeo above.
                                </Text>
                            </div>
                        ) : (
                            <Droppable
                                id="videoBank-container"
                                data={{ container: "videoBank", index: tempVideoBankState.length, isContainer: true }}
                                layout="vertical"
                                showPlaceholder={false}
                            >
                                <ol className="video-bank-list custom-scrollbar-y drag-list-vertical drag-area-full list-reset">
                                    {tempVideoBankState.map((video, index) => (
                                        <React.Fragment key={`videoBank-${video.embedId}`}>
                                            {videoBankPlaceholderIndex === index &&
                                                renderPlaceholder(`videoBank-before-${video.embedId}`)}
                                            <Draggable
                                                id={`videoBank-${video.embedId}`}
                                                data={{ container: "videoBank", index }}
                                                droppableData={{ container: "videoBank", index }}
                                            >
                                                <li
                                                    className={`draggable-video drag-draggable ${isActiveItem("videoBank", index) ? "drag-source-hidden" : ""}`}
                                                >
                                                    <VideoRow
                                                        index={index}
                                                        video={video}
                                                        removeable={true}
                                                        removeFromVideoBank={() => {
                                                            const updated = Array.from(tempVideoBankState);
                                                            updated.splice(index, 1);
                                                            updateVideoBankState(updated);
                                                        }}
                                                        updateTempState={(newTitle) => {
                                                            updateVideoTitleInVideoBank(
                                                                newTitle,
                                                                video,
                                                                index
                                                            );
                                                        }}
                                                    />
                                                </li>
                                            </Draggable>
                                        </React.Fragment>
                                    ))}
                                    {videoBankPlaceholderIndex === tempVideoBankState.length &&
                                        renderPlaceholder("videoBank-after")}
                                </ol>
                            </Droppable>
                        )}
                    </div>
                </div>

                <div className="collection-creator-new-module flex flex-col module-creator">
                    <Title level={4}>Module Details</Title>

                    <div className="module-creator-content flex flex-row full-height gap-25 border p-10">
                        <div className="module-videos-section w-50">
                            <div className="videos-in-module-list-container p-10 h-100 flex flex-col full-height border-dashed">
                                <Droppable
                                    id="videos-container"
                                    data={{ container: "videos", index: tempModuleState.videos.length, isContainer: true }}
                                    layout="grid"
                                    gridColumns={3}
                                    showPlaceholder={false}
                                >
                                    <div
                                        className={`collection-creator-videos-in-module-list drag-list-grid drag-area-full ${tempModuleState.videos.length === 0 ? "no-video" : ""} ${videosPlaceholderIndex !== null && tempModuleState.videos.length === 0 ? "is-dragging" : ""}`}
                                    >
                                        {tempModuleState.videos.map((video, index) => (
                                            <React.Fragment key={`videos-${video.embedId}`}>
                                                {videosPlaceholderIndex === index &&
                                                    renderPlaceholder(`videos-before-${video.embedId}`)}
                                                <Draggable
                                                    id={`videos-${video.embedId}`}
                                                    data={{ container: "videos", index }}
                                                    droppableData={{ container: "videos", index }}
                                                >
                                                    <div
                                                        className={`draggable-video drag-draggable ${isActiveItem("videos", index) ? "drag-source-hidden" : ""}`}
                                                        style={{ width: "100%" }}
                                                    >
                                                        <VideoRow
                                                            index={index}
                                                            video={video}
                                                            removeable={true}
                                                            removeFromVideoBank={() => {
                                                                const updatedModule = { ...tempModuleState };
                                                                const videos = Array.from(updatedModule.videos);
                                                                const [removedVideo] = videos.splice(index, 1);
                                                                updatedModule.videos = videos;
                                                                updateTempModuleState(updatedModule);
                                                                if (removedVideo) {
                                                                    updateVideoBankState([
                                                                        ...tempVideoBankState,
                                                                        removedVideo
                                                                    ]);
                                                                }
                                                            }}
                                                            updateTempState={(newTitle) => {
                                                                updateVideoTitleInVideoBank(
                                                                    newTitle,
                                                                    video,
                                                                    index
                                                                );
                                                            }} />
                                                    </div>
                                                </Draggable>
                                            </React.Fragment>
                                        ))}
                                        {videosPlaceholderIndex === tempModuleState.videos.length &&
                                            renderPlaceholder("videos-after")}
                                    </div>
                                </Droppable>
                            </div>
                        </div>

                        <div className="module-details-form-section flex flex-col full-height w-50">
                            <ModuleConfigForm
                                module={tempModuleState}
                                moduleIdentity={
                                    tempModuleState?.key ??
                                    tempModuleState?.id ??
                                    "module-details"
                                }
                                policyModel={policyModel}
                                authorsDict={authorsDict}
                                currentUser={currentUser}
                                collectionCategories={collectionCategories}
                                fetchCreatorList={fetchCreatorList}
                                onSubmit={handleOnEditModuleFormFinish}
                                onCancel={() => history.push('/creator/home')}
                                submitLabel={
                                    mode === "edit"
                                        ? "Update Module"
                                        : "Next: Review Changes"
                                }
                                disableSubmit={tempModuleState.videos.length < 1}
                                showActions={true}
                            />
                        </div>
                    </div>

                </div>

            </div>
            <DragOverlay>
                {(() => {
                    const activeItem = getActiveItem();
                    if (!activeItem) return null;
                    return (
                        <div className="drag-overlay-card">
                            <VideoRow video={activeItem} />
                        </div>
                    );
                })()}
            </DragOverlay>
        </DndContext>
    </div>
}

const mapStateToProps = state => {
    return {
        collectionCategories: state.collectionReducer.collectionCategories,
        policyModel: state.contentCreationReducer.policyModel,
    }
}

export default connect(mapStateToProps, {})(ModuleDetailsStep);
